<template>
  <Loading v-if="componentLoading" :style="style" />
  <div v-else class="amap-chart" :style="style">
    <div class="amap-container" ref="mapContainer"></div>
    <div id="amap-control"></div>
    <div id="amap-info-window"></div>
    <AMapMeasure @measure-complete="onMeasureComplete" @measure-cancel="onMeasureCancel" />
    <AMapTrafficLayer ref="trafficLayer" :show="option.showTraffic" @traffic-toggle="onTrafficToggle" />
    <AMapTextLayer ref="textLayer" :show="option.showText" @text-toggle="onTextToggle" />
    <AMapWaterLayer ref="waterLayer" :show="option.showWater" @water-toggle="onWaterToggle" />
    <AMapBuildingLayer ref="buildingLayer" :show="option.showBuilding" @building-toggle="onBuildingToggle" />
    <AMapCenterLayer ref="centerLayer" @center-reset="onCenterReset" />
    <AMapPolygonCover ref="polygonCover" :geojsonUrl="option.coverGeo" :maskOutside="option.maskOutside" />
  </div>
  <slot></slot>
</template>

<script lang="ts" setup>
// 当前模块引入
import Loading from "@p-v/packages/Loading.vue"
import AMapMeasure from "./messure.vue"
import AMapTrafficLayer from "./trafficLayer.vue"
import AMapTextLayer from "./textLayer.vue"
import AMapWaterLayer from "./waterLayer.vue"
import AMapBuildingLayer from "./buildingLayer.vue"
import AMapCenterLayer from "./centerLayer.vue"
import AMapPolygonCover from "./polygon-cover.vue"
import { ref, provide, watch, nextTick, onUnmounted } from "vue"
import { cloneDeep, defaultsDeep } from "lodash-es"
import { requestAdapter } from "@p-v/api"
import config from "./config"
import { getAMap } from "@p-v/utils/amap"
import { cssToJson, jsonToCss } from "@p-v/utils/cssJson"

defineOptions({
  name: "AMapChart",
})

const emits = defineEmits([
  "click",
  "measure-complete",
  "measure-cancel",
  "traffic-toggle",
  "text-toggle",
  "water-toggle",
  "building-toggle",
  "center-reset",
])

interface ComponentProps {
  style: { [key: string]: string }
  option?: {
    securityKey: string
    styleJson?: string
    showTraffic?: boolean
    showBuilding?: boolean
    coverGeo?: string
    maskOutside?: boolean
    amap?: {
      center: [number, number]
      zoom: number
      features?: string[]
      [key: string]: any
    }
    mapOpt?: {
      poiText?: boolean
      poiIcon?: boolean
      heading?: number
      pitch?: number
      rotateEnable?: boolean
      pitchEnable?: boolean
      skyColors?: string[]
      viewMode?: "2D" | "3D"
      mapStyle?: string
    }
    [key: string]: any
  }
}

const props = withDefaults(defineProps<ComponentProps>(), {
  style: () => ({}),
  option: () => ({
    securityKey: "",
    styleJson: "",
    showTraffic: false,
    showBuilding: true,
    coverGeo: undefined,
    maskOutside: true,
    mapOpt: {
      poiText: true,
      poiIcon: true,
      viewMode: "2D",
      mapStyle: "amap://styles/normal",
      rotateEnable: false,
      pitchEnable: false,
    },
  }),
})

const componentLoading = ref<boolean>(true)
const mapContainer = ref<HTMLElement | null>(null)
const amap = ref<any>(null)

// 测距功能相关处理方法
const onMeasureComplete = (measureData: { points: number[][]; totalDistance: number }) => {
  emits("measure-complete", measureData)
}

const onMeasureCancel = () => {
  emits("measure-cancel")
}

const trafficLayer = ref<InstanceType<typeof AMapTrafficLayer> | null>(null)
const textLayer = ref<InstanceType<typeof AMapTextLayer> | null>(null)
const waterLayer = ref<InstanceType<typeof AMapWaterLayer> | null>(null)
const buildingLayer = ref<InstanceType<typeof AMapBuildingLayer> | null>(null)
const centerLayer = ref<InstanceType<typeof AMapCenterLayer> | null>(null)
const polygonCover = ref<InstanceType<typeof AMapPolygonCover> | null>(null)

// 交通图层功能相关处理方法
const onTrafficToggle = (isVisible: boolean) => {
  emits("traffic-toggle", isVisible)
}

// 文字图层功能相关处理方法
const onTextToggle = (isVisible: boolean) => {
  emits("text-toggle", isVisible)
}

// 水系图层功能相关处理方法
const onWaterToggle = (isVisible: boolean) => {
  emits("water-toggle", isVisible)
}

// 建筑图层功能相关处理方法
const onBuildingToggle = (isVisible: boolean) => {
  emits("building-toggle", isVisible)
}

// 地图中心点重置功能处理方法
const onCenterReset = (centerPoint: { lng: number; lat: number }) => {
  emits("center-reset", centerPoint)
}

// 清除版权信息
const clearCopyright = () => {
  try {
    // 查找带有高德地图logo的元素
    const mapEl = mapContainer.value
    if (!mapEl) return false

    const logoEls = mapEl.querySelectorAll(".amap-logo, .amap-copyright")
    if (logoEls.length > 0) {
      logoEls.forEach(el => {
        ;(el as HTMLElement).remove()
      })
      return true
    }
    return false
  } catch (error) {
    console.error("清除版权信息失败", error)
    return false
  }
}
// 地图初始化配置
const mapConfig = ref(cloneDeep(defaultsDeep(props.option, config)))

const centerPoint = ref<{ lng: number; lat: number }>({
  lng: mapConfig.value.amap.center[0],
  lat: mapConfig.value.amap.center[1],
})

// 设置中心点
const setCenter = (point: { lng: number; lat: number }) => {
  centerPoint.value = point
  amap.value?.setCenter([point.lng, point.lat])
}
const mapFeatures = ref<string[]>(["road", "building", "bg", "point"])

provide("amap", amap)
provide("mapFeatures", mapFeatures)
provide("centerPoint", centerPoint)
provide("coordinateSystem", "amap")

// 更新地图上的系列数据
const updateSeries = (data: { id: string }) => {
  const { id } = data
  const series = mapConfig.value.series
  const index = series.findIndex((item: Record<string, unknown>) => item.id === id)
  if (index > -1) {
    series.splice(index, 1)
    mapConfig.value.series = series
  } else {
    mapConfig.value.series.push(data)
  }
}

provide("updateSeries", updateSeries)

const markInfoBoxActiveComponentId = ref<string>("")
provide("markInfoBoxActiveComponentId", markInfoBoxActiveComponentId)

// 进入加载状态
const loading = () => {
  componentLoading.value = true
}

// 监听地图点击事件
watch(
  () => amap.value,
  val => {
    if (val) {
      amap.value.on("click", () => {
        markInfoBoxActiveComponentId.value = ""
        emits("click")
      })
    }
  },
  {
    immediate: true,
  },
)

// 监听遮罩URL变化
watch(
  () => props.option.coverGeo,
  newVal => {
    if (polygonCover.value) {
      if (newVal) {
        // 有URL时设置并显示
        polygonCover.value.setGeoJsonUrl(newVal)
        polygonCover.value.show()
      } else {
        // 没有URL时隐藏
        polygonCover.value.hide()
      }
    }
  },
)

// 设置数据
const setData = async (data: ComponentProps) => {
  if (!props.option.securityKey && !data.option?.securityKey) {
    console.error("必须提供高德地图安全密钥(securityKey)")
    return
  }

  await getAMap(data.option?.securityKey || props.option.securityKey)

  const { option } = data

  if (option?.styleJson || props.option.styleJson) {
    try {
      await requestAdapter<{ [key: string]: string | number | boolean }>({
        url: (option?.styleJson || props.option.styleJson)!,
        method: "GET",
      })
    } catch (error) {
      console.error("加载样式数据失败", error)
    }
  }

  // 合并配置
  mapConfig.value = defaultsDeep(option || {}, props.option, config)
  // 地图加载完成
  componentLoading.value = false

  // 延迟执行以确保DOM已渲染
  nextTick(() => {
    if (!mapContainer.value) return

    // 初始化高德地图
    if (!amap.value) {
      const mapOptions = {
        viewMode: props.option.mapOpt?.viewMode || "2D", // 2D/3D模式
        zoom: mapConfig.value.amap.zoom,
        center: mapConfig.value.amap.center,
        mapStyle: props.option.mapOpt?.mapStyle || "amap://styles/normal",
        pitch: props.option.mapOpt?.pitch || 0, // 俯仰角
        rotateEnable: props.option.mapOpt?.rotateEnable || false, // 允许旋转
        pitchEnable: props.option.mapOpt?.pitchEnable || false, // 允许俯仰
      }

      console.log("初始化高德地图", mapOptions, props)

      amap.value = new window.AMap.Map(mapContainer.value, mapOptions)

      amap.value.setFeatures(mapFeatures.value)
      // 设置地图功能
      if (props.option.showTraffic) {
        trafficLayer.value?.showTraffic()
      }

      // 设置视角
      if (props.option.mapOpt?.heading !== undefined) {
        amap.value.setRotation(props.option.mapOpt.heading)
      }

      // if (props.mapOpt?.pitch !== undefined && props.mapOpt.viewMode === "3D") {
      //   amap.value.setPitch(props.mapOpt.pitch)
      // }

      console.log("地图配置", props)
      if (props.option.showBuilding) {
        // 如果配置要求显示建筑物，通过buildingLayer组件控制
        buildingLayer.value?.showBuildingLayer()
      } else {
        // 如果配置不显示建筑物，则从features中移除building
        buildingLayer.value?.hideBuildingLayer()
      }

      // 设置POI
      if (props.option.mapOpt?.poiText === false) {
        textLayer.value?.hideTextLayer()
      }

      // 设置地图多边形遮罩
      if (data.option?.coverGeo || props.option.coverGeo) {
        nextTick(() => {
          // 如果提供了新的 GeoJSON URL，则使用它
          const geoJsonUrl = data.option?.coverGeo || props.option.coverGeo || ""
          polygonCover.value?.setGeoJsonUrl(geoJsonUrl)

          // 如果传入了maskOutside参数，会自动生效，因为组件会监听props变化
        })
      }

      // 设置背景色
      const amapContainer = mapContainer.value
      if (amapContainer) {
        const style = amapContainer.getAttribute("style") || ""
        // 结构化style
        const styleObj = cssToJson(style)
        // 设置背景色
        styleObj["background-color"] = props.style.backgroundColor || "#fff"
        // 设置样式
        amapContainer.setAttribute("style", jsonToCss(styleObj))
      }

      // 尝试清除版权信息
      let tryTimes = 0
      const removeTimer = setInterval(() => {
        const isRemoved = clearCopyright()
        if (isRemoved || tryTimes > 10) {
          clearInterval(removeTimer)
        }
        tryTimes++
      }, 300)
    }
  })
}

// 组件销毁时释放地图实例
onUnmounted(() => {
  if (amap.value) {
    amap.value.destroy()
    amap.value = null
  }
})

// 暴露方法
defineExpose({
  setData,
  loading,
  amap,
  setCenter,
  polygonCover,
})
</script>

<style lang="scss">
.amap-chart {
  position: relative;
  height: 100%;
  width: 100%;
  overflow: hidden;
}

#amap-control {
  position: absolute;
  top: 5px;
  right: 5px;
  z-index: 100;
}

#amap-info-window {
  position: absolute;
  z-index: 1000;
  // display: none; /* 默认隐藏 */
  bottom: 5px;
  right: 5px;
}
.amap-container {
  height: 100%;
  width: 100%;
  position: relative;
}
</style>
